import { useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { getHojeBR } from '../utils/dataBR';

export function calculateStreak(logs) {
  const completed = logs.filter(l => l.completed);
  if (!completed.length) return 0;
  const dates = completed
    .map(l => new Date(l.date + 'T00:00:00'))
    .sort((a, b) => b - a);
  let count = 0;
  let cursor = new Date(getHojeBR() + 'T00:00:00');
  for (const d of dates) {
    const diff = Math.round((cursor - d) / 86400000);
    if (diff === 0 || diff === 1) { count++; cursor = d; }
    else break;
  }
  return count;
}

export function useJournal(userId) {
  const [logs, setLogs] = useState([]);
  const [streak, setStreak] = useState(0);
  const [hasCheckedToday, setHasCheckedToday] = useState(false);
  const [todayNote, setTodayNote] = useState('');
  const [todayMood, setTodayMood] = useState(null);
  const [todayMoodWord, setTodayMoodWord] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async (uid) => {
    const id = uid || userId;
    if (!id) return [];
    setLoading(true);
    const { data, error } = await supabase
      .from('daily_logs')
      .select('date, note, completed, mood_score, mood_word')
      .eq('user_id', id)
      .order('date', { ascending: false });

    if (error) { console.error('fetchData error:', error); setLoading(false); return []; }

    const today = getHojeBR();
    const todayLog = data?.find(l => l.date === today);

    setLogs(data || []);
    setStreak(calculateStreak(data || []));
    setHasCheckedToday(!!todayLog?.completed);
    if (todayLog) {
      setTodayNote(todayLog.note || '');
      setTodayMood(todayLog.mood_score || null);
      setTodayMoodWord(todayLog.mood_word || null);
    }
    setLoading(false);
    return data || [];
  }, [userId]);

  const checkIn = async (mood) => {
    if (!userId) return { error: 'Não autenticado' };
    const today = getHojeBR();
    const { error } = await supabase
      .from('daily_logs')
      .insert([{ user_id: userId, date: today, completed: true, note: '', mood_score: mood || null }]);
    if (!error) {
      setHasCheckedToday(true);
      setTodayMood(mood || null);
    }
    return { error };
  };

  // Salva a palavra de sentimento e o score numérico no log de hoje
  const saveSentimento = async (word, score) => {
    if (!userId) return { error: 'Não autenticado' };
    const today = getHojeBR();

    // Tenta atualizar linha existente primeiro
    const { data: updated, error: updateError } = await supabase
      .from('daily_logs')
      .update({ mood_word: word, mood_score: score, completed: true })
      .eq('user_id', userId)
      .eq('date', today)
      .select('id');

    if (!updateError && updated?.length > 0) {
      setTodayMoodWord(word);
      setTodayMood(score);
      setHasCheckedToday(true);
      return { error: null };
    }

    // Sem linha ainda: insere
    const { error: insertError } = await supabase
      .from('daily_logs')
      .insert([{ user_id: userId, date: today, completed: true, mood_word: word, mood_score: score }]);
    if (!insertError) {
      setTodayMoodWord(word);
      setTodayMood(score);
      setHasCheckedToday(true);
    }
    return { error: insertError };
  };

  const saveNote = async (note) => {
    if (!userId) return { error: 'Não autenticado' };
    const today = getHojeBR();
    const wordCount = note.trim() ? note.trim().split(/\s+/).length : 0;
    const { error } = await supabase
      .from('daily_logs')
      .update({ note, word_count: wordCount })
      .eq('user_id', userId)
      .eq('date', today);
    if (!error) setTodayNote(note);
    return { error };
  };

  return { logs, streak, hasCheckedToday, todayNote, todayMood, todayMoodWord, loading, fetchData, checkIn, saveSentimento, saveNote };
}
